/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import SaveIcon from '@mui/icons-material/Save';
import SourceIcon from '@mui/icons-material/Source';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { Box, IconButton, Tooltip } from "@mui/material";
import {MRT_GlobalFilterTextField, MRT_TableInstance, MRT_ToggleFiltersButton, MRT_ToggleGlobalFilterButton} from "material-react-table";
import { tableButtonGray } from "../../../../../../constants/colors";
import { useHasAccessToRoute, useHasWriteAccess } from "../../../../../../redux/ducks/AccessControl/accessControlHelpers";
import { useAppDispatch, useAppSelector } from "../../../../../../redux/hooks";
import {EAdasProgramsLoadingStateTypes, selectAdasProgramsArchive, selectIsCoverageRequirementsReadOnlyState} from "../../../../../../redux/ducks/AdasPrograms/slice";
import { manageDataSourcesDialogSlice } from "../Dialogs/ManageDataSourcesDialog";
import { AdasProgramsReportPages, ReportsTableData } from "../constants";
import { newReportDialogSlice } from "../Dialogs/NewReportDialog";
import { ArgosAddIcon, ArgosDeleteIcon, ArgosReloadIcon } from "../../../../../components/Icons/ArgosActionIcons";
import { deleteReportDialogSlice } from "../Dialogs/DeleteReportDialog";
import { updateReportDialogSlice } from "../Dialogs/UpdateReportDialog";
import { SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS } from '../../../../SensingSystemArchitectureDesign/constants';
import React from "react";
import {isEmpty} from "lodash";
import {adasProgramsItemDetailsSocketSlice} from "../../AdasProgramsItemDetails";
import Loader from "../../../../../components/Loader/Loader";
import ArticleIcon from '@mui/icons-material/Article';
import {generateCombinedReportDialogSlice} from "../Dialogs/GenerateCombinedReportDialog";


interface RightToolbarActionsProps {
  page: AdasProgramsReportPages
  getReportsTable: (onlyIfOutdated: boolean) => void
  saveTable: () => void
  isSaveDisabled: boolean
  isDeleteDisabled: boolean
  anyOutdatedReports: boolean
  isGetReportsTableLoading: boolean
}

/**
 * Generates the top-right row of toolbar actions for a table.
 */
export const RightToolbarActions = (table: MRT_TableInstance<ReportsTableData>, props: RightToolbarActionsProps) => {
  const { page, getReportsTable, saveTable, isSaveDisabled, isDeleteDisabled, anyOutdatedReports, isGetReportsTableLoading } = props

  const dispatch = useAppDispatch()
  const hasWriteAccess = useHasWriteAccess()
  const hasSensingArchitectureAccess = useHasAccessToRoute(SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.landing.route)
  const reduxIsProgramArchive = useAppSelector(state => selectAdasProgramsArchive(state))
  const isReadOnlyProgram = useAppSelector(selectIsCoverageRequirementsReadOnlyState) || reduxIsProgramArchive
  const isNewReportDisabled = page === AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT && !hasSensingArchitectureAccess

  const selectedReports = useAppSelector(updateReportDialogSlice[page].selectedReports)
  const isUpdatingReports = useAppSelector(state => adasProgramsItemDetailsSocketSlice[page].selectLoadingState(state)?.[EAdasProgramsLoadingStateTypes.UPDATE_REPORTS]?.isLoading ?? false)
  const isUpdateReportsDisabled = isEmpty(selectedReports) || isGetReportsTableLoading || isReadOnlyProgram || isUpdatingReports

  return (
    <Box style={{}}>
      {hasWriteAccess && !isReadOnlyProgram && <Tooltip title="Download Combined Report" >
        <span>
          <IconButton
          onClick={() => dispatch(generateCombinedReportDialogSlice[page].setIsDialogOpen(true))}
          size="medium"
          disabled={isUpdateReportsDisabled}
        >
          <ArticleIcon
            style={{color: isUpdateReportsDisabled ? undefined : tableButtonGray}}
            color={isUpdateReportsDisabled ? 'disabled' : undefined}
          />
        </IconButton>
        </span>
      </Tooltip>}
      {hasWriteAccess && !isReadOnlyProgram && <Tooltip title="Update Selected Reports" >
        <span>
          <IconButton
          onClick={() => dispatch(updateReportDialogSlice[page].setIsDialogOpen(true))}
          size="medium"
          disabled={isUpdateReportsDisabled}
        >
          <UpgradeIcon
            style={{color: isUpdateReportsDisabled ? undefined : tableButtonGray}}
            color={isUpdateReportsDisabled ? 'disabled' : undefined}
          />
            <Loader isLoading={isUpdatingReports} />
        </IconButton>
        </span>
      </Tooltip>}
      {hasWriteAccess && !isReadOnlyProgram && page !== AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT && <Tooltip title="Manage Data Sources">
        <IconButton
          onClick={() => dispatch(manageDataSourcesDialogSlice[page].setIsDialogOpen(true))}
          size="medium"
        >
          <SourceIcon
            style={{color: tableButtonGray}}
          />
        </IconButton>
      </Tooltip>}
      {hasWriteAccess && !isReadOnlyProgram && <Tooltip title="New">
        <IconButton
          onClick={() => dispatch(newReportDialogSlice[page].setIsDialogOpen(true))}
          size="medium"
          disabled={isNewReportDisabled}
        >
          <ArgosAddIcon
            style={{color: isNewReportDisabled ? undefined : tableButtonGray}}
            color={isNewReportDisabled ? 'disabled' : undefined}
          />
        </IconButton>
      </Tooltip>}
      {hasWriteAccess && !isReadOnlyProgram && <Tooltip title="Save">
        <IconButton
          onClick={() => saveTable()}
          size="medium"
          disabled={isSaveDisabled}
        >
          <SaveIcon
            style={{color: isSaveDisabled ? undefined : tableButtonGray}}
            color={isSaveDisabled ? 'disabled' : undefined}
          />
        </IconButton>
      </Tooltip>}
      <Tooltip title="Reload">
        <IconButton
          onClick={() => {
            table.resetSorting(true)
            getReportsTable(false)
          }}
          size="medium"
        >
          <ArgosReloadIcon style={{color: tableButtonGray}} />
        </IconButton>
      </Tooltip>
      {hasWriteAccess && !isReadOnlyProgram && <Tooltip title="Delete">
        <IconButton
          onClick={() => dispatch(deleteReportDialogSlice[page].setIsDialogOpen(true))}
          size="medium"
          disabled={isDeleteDisabled}
        >
          <ArgosDeleteIcon
            style={{color: isDeleteDisabled ? undefined : tableButtonGray}}
            color={isDeleteDisabled ? 'disabled' : undefined}
          />
        </IconButton>
      </Tooltip>}
      <MRT_ToggleGlobalFilterButton table={table} style={{color: tableButtonGray}} />
      <MRT_ToggleFiltersButton table={table} style={{color: tableButtonGray}} />
    </Box>
  )
}
